import React, { useMemo, useState } from 'react';
import { formatMontant } from '../../utils/helpers';
import { MOIS_LABELS } from './comptaUtils';

const STATUT_OPTIONS = ['en_attente', 'confirme', 'regularise', 'contact_pris'];
const STATUT_LABELS  = { en_attente: '⏳ En attente', confirme: '❌ Confirmé', regularise: '✅ Régularisé', contact_pris: '📞 Relance effectuée' };
const STATUT_COLORS  = { en_attente: '#d97706', confirme: '#991b1b', regularise: '#166534', contact_pris: '#1d4ed8' };

export default function OngletDefauts({ adherents, comptabilite, selectedYear, saveComptaOperation }) {
  const [editNote, setEditNote] = useState(null); // { mois, opId }
  const [noteText, setNoteText] = useState('');

  // ── Collect all Type-B ops for selected year ──
  const allDefauts = useMemo(() => {
    const results = [];
    for (let m = 1; m <= 12; m++) {
      const ops = comptabilite[selectedYear]?.[m]?.operations || [];
      ops.filter(o => o.type === 'B').forEach(op => {
        results.push({ ...op, mois: m });
      });
    }
    return results.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [comptabilite, selectedYear]);

  // ── Group by adherent ──
  const byAdherent = useMemo(() => {
    const map = {};
    for (const op of allDefauts) {
      const key = op.adherentId || `__inconnu_${op.libelleBrut}`;
      if (!map[key]) map[key] = { adherentId: op.adherentId, libelleBrut: op.libelleBrut, ops: [] };
      map[key].ops.push(op);
    }
    return Object.values(map).sort((a, b) => b.ops.length - a.ops.length);
  }, [allDefauts]);

  const totalNonRecouvre = useMemo(() =>
    allDefauts.filter(o => o.statut !== 'regularise').reduce((s, o) => s + o.debit, 0),
    [allDefauts]);

  const updateStatut = (mois, opId, statut) => {
    const ops = comptabilite[selectedYear]?.[mois]?.operations || [];
    const op = ops.find(o => o.id === opId);
    if (op) saveComptaOperation(selectedYear, mois, { ...op, statut });
  };

  const saveNote = (mois, opId) => {
    const ops = comptabilite[selectedYear]?.[mois]?.operations || [];
    const op = ops.find(o => o.id === opId);
    if (op) saveComptaOperation(selectedYear, mois, { ...op, note: noteText });
    setEditNote(null); setNoteText('');
  };

  if (!allDefauts.length) {
    return (
      <div className="empty-state" style={{ padding: '60px 0' }}>
        <div className="empty-state-icon">✅</div>
        <div className="empty-state-text">Aucun impayé détecté pour {selectedYear}</div>
        <div style={{ fontSize:12, color:'#9ca3af', marginTop:8 }}>Les impayés apparaissent après l'import d'un relevé BFC contenant des lignes "IMPAYE PREL"</div>
      </div>
    );
  }

  return (
    <div>
      {/* KPI */}
      <div className="grid-3 mb-4" style={{ gap:14 }}>
        <div className="stat-card" style={{ borderTopColor:'#991b1b' }}>
          <div className="stat-label">Total impayés {selectedYear}</div>
          <div className="stat-value" style={{ color:'#991b1b' }}>{allDefauts.length}</div>
          <div className="stat-sub">{byAdherent.length} adhérent(s) concerné(s)</div>
        </div>
        <div className="stat-card" style={{ borderTopColor:'#d97706' }}>
          <div className="stat-label">Non recouvrés</div>
          <div className="stat-value" style={{ color:'#d97706' }}>{formatMontant(totalNonRecouvre)}</div>
          <div className="stat-sub">{allDefauts.filter(o => o.statut !== 'regularise').length} impayé(s) en attente</div>
        </div>
        <div className="stat-card" style={{ borderTopColor:'#166534' }}>
          <div className="stat-label">Régularisés</div>
          <div className="stat-value" style={{ color:'#166534' }}>{allDefauts.filter(o => o.statut === 'regularise').length}</div>
          <div className="stat-sub">{formatMontant(allDefauts.filter(o => o.statut === 'regularise').reduce((s,o)=>s+o.debit,0))} recouvrés</div>
        </div>
      </div>

      {/* Alert 3+ défauts */}
      {byAdherent.some(g => g.ops.length >= 3) && (
        <div className="alert alert-danger mb-4">
          <span>🚨</span>
          <div>
            <strong>Adhérent(s) avec 3 défauts ou plus</strong> — action recommandée :<br />
            {byAdherent.filter(g => g.ops.length >= 3).map(g => {
              const adh = adherents.find(a => a.id === g.adherentId);
              return <span key={g.adherentId || g.libelleBrut} style={{ marginRight:12 }}>
                {adh ? `${adh.prenom} ${adh.nom}` : g.libelleBrut} ({g.ops.length} défauts)
              </span>;
            })}
          </div>
        </div>
      )}

      {/* Per-adherent cards */}
      {byAdherent.map(group => {
        const adh = adherents.find(a => a.id === group.adherentId);
        const count = group.ops.length;
        const alertColor = count >= 3 ? '#991b1b' : count >= 2 ? '#d97706' : '#6b7280';
        const alertBg    = count >= 3 ? '#fee2e2' : count >= 2 ? '#fef3c7' : '#f9fafb';

        return (
          <div key={group.adherentId || group.libelleBrut} className="card mb-3"
            style={{ borderLeft:`4px solid ${alertColor}` }}>
            <div className="card-header" style={{ background: alertBg }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div className="adh-avatar" style={{ width:38, height:38, fontSize:14, background: alertColor, color:'#fff' }}>
                  {adh ? (adh.prenom[0] + adh.nom[0]).toUpperCase() : '?'}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:14 }}>
                    {adh ? `${adh.prenom} ${adh.nom}` : <em style={{ color:'#9ca3af' }}>{group.libelleBrut} (non reconnu)</em>}
                  </div>
                  {adh && <div style={{ fontSize:12, color:'#6b7280' }}>{adh.metier || '—'}</div>}
                </div>
                <span style={{ marginLeft:'auto', background: alertColor, color:'#fff', borderRadius:999, padding:'3px 12px', fontSize:12, fontWeight:700 }}>
                  {count >= 3 ? '🚨' : count >= 2 ? '⚠️' : '!'} {count} défaut{count > 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Mois</th><th>Date rejet</th><th style={{ textAlign:'right' }}>Montant</th>
                    <th>Statut</th><th>Note</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.ops.map(op => (
                    <tr key={op.id}>
                      <td style={{ fontSize:12 }}>{MOIS_LABELS[op.mois - 1]}</td>
                      <td style={{ fontSize:12, color:'#6b7280' }}>{op.dateAffichage || op.date?.slice(5)?.replace('-','/')}</td>
                      <td style={{ textAlign:'right', fontWeight:700, color:'#991b1b', fontSize:12 }}>{formatMontant(op.debit)}</td>
                      <td>
                        <select className="form-control" style={{ fontSize:11, padding:'3px 6px' }}
                          value={op.statut}
                          onChange={e => updateStatut(op.mois, op.id, e.target.value)}>
                          {STATUT_OPTIONS.map(s =>
                            <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
                        </select>
                      </td>
                      <td style={{ maxWidth:180 }}>
                        {editNote?.opId === op.id ? (
                          <div style={{ display:'flex', gap:4 }}>
                            <input className="form-control" style={{ fontSize:11 }}
                              value={noteText} onChange={e => setNoteText(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveNote(op.mois, op.id); }} autoFocus />
                            <button className="btn btn-primary btn-xs" onClick={() => saveNote(op.mois, op.id)}>✓</button>
                            <button className="btn btn-ghost btn-xs" onClick={() => setEditNote(null)}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                            <span style={{ fontSize:11, color:'#6b7280', flex:1 }}>{op.note || <em style={{ color:'#bbb' }}>Aucune note</em>}</span>
                            <button className="btn btn-ghost btn-xs" onClick={() => { setEditNote({ opId: op.id }); setNoteText(op.note || ''); }}>✏️</button>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:4 }}>
                          {op.statut !== 'regularise' && (
                            <button className="btn btn-outline btn-xs" style={{ fontSize:10, color:'#166534', borderColor:'#bbf7d0' }}
                              onClick={() => updateStatut(op.mois, op.id, 'regularise')}>
                              ✅ Régularisé
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
